/**
 * User preferences kept in localStorage (UI state plan, phase 1).
 *
 * Only real preferences belong here (things the user chose on this device);
 * never who the user is or what they may do: that is the session, in memory
 * only (auth/session.ts). Every key is namespaced `pnet.pref.<name>`, every
 * access is guarded (storage can be blocked or full) and every read is
 * validated, so a value written by an older build or edited by hand cannot
 * break the page.
 *
 * New code uses these helpers instead of raw `localStorage`. The existing
 * page-to-page keys (`brand_id`, `tabType`, ...) are not preferences and are
 * not migrated here (they move to the URL in phase 2).
 */
export const PREF_PREFIX = 'pnet.pref.';

export const prefKey = (name: string): string => `${PREF_PREFIX}${name}`;

const storage = (): Storage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null; // storage blocked
  }
};

/**
 * The stored preference when `isValid` accepts it, else `fallback`
 * (missing, unreadable, not JSON, or not the expected shape).
 */
export function readPref<T>(name: string, isValid: (value: unknown) => boolean, fallback: T): T {
  try {
    const raw = storage()?.getItem(prefKey(name));
    if (raw === null || raw === undefined) return fallback;
    const value: unknown = JSON.parse(raw);
    return isValid(value) ? (value as T) : fallback;
  } catch {
    return fallback;
  }
}

/** Stores the preference; false when storage is unavailable or full. */
export function writePref(name: string, value: unknown): boolean {
  try {
    const target = storage();
    if (!target) return false;
    target.setItem(prefKey(name), JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function removePref(name: string): void {
  try {
    storage()?.removeItem(prefKey(name));
  } catch {
    /* storage unavailable */
  }
}
